/**
 * Local-only player profile helpers.
 *
 * Each browser/device gets a stable uuidv4 the first time a player connects;
 * we persist it (and the player's last-used display name / color / avatar)
 * to localStorage so that:
 *  - reopening a room URL on the same device restores the same `uid`
 *  - joining a fresh room pre-populates name/color/avatar from last time
 *
 * No network. No React. Safe to import from anywhere.
 */

import { v4 as uuidv4 } from 'uuid'
import type { PlayerColor } from '@mdev/common'

/** Versioned localStorage key — bump suffix when the shape changes. */
export const LOCAL_STORAGE_KEY = 'sy:player-profile:v1'

interface StoredEnvelope {
  uid: string
  profile: StoredProfile | null
}

export interface StoredProfile {
  displayName: string
  color: PlayerColor
  avatar: string
}

function safeParse( raw: string | null ): StoredEnvelope | null {
  if ( !raw ) return null
  try {
    const parsed = JSON.parse( raw ) as StoredEnvelope
    if ( typeof parsed?.uid !== 'string' ) return null
    return parsed
  } catch {
    return null
  }
}

function readEnvelope(): StoredEnvelope | null {
  if ( typeof window === 'undefined' ) return null
  return safeParse( window.localStorage.getItem( LOCAL_STORAGE_KEY ) )
}

function writeEnvelope( envelope: StoredEnvelope ): void {
  if ( typeof window === 'undefined' ) return
  window.localStorage.setItem( LOCAL_STORAGE_KEY, JSON.stringify( envelope ) )
}

/**
 * Read the current player's stable uid from localStorage, generating and
 * persisting a new uuidv4 on first call. Subsequent calls return the same
 * value for the lifetime of localStorage on this device.
 */
export function getOrCreateLocalUid(): string {
  const existing = readEnvelope()
  if ( existing ) return existing.uid

  const uid = uuidv4()
  writeEnvelope( { uid, profile: null } )
  return uid
}

/** Read the last-saved profile, or `null` if the user hasn't picked one yet. */
export function getStoredProfile(): StoredProfile | null {
  return readEnvelope()?.profile ?? null
}

/** Persist the given profile alongside the existing uid. */
export function saveProfile( profile: StoredProfile ): void {
  const envelope = readEnvelope()
  const uid = envelope?.uid ?? uuidv4()
  writeEnvelope( { uid, profile } )
}

/** Wipe local profile state — exposed for "switch player" / debug flows. */
export function clearLocalProfile(): void {
  if ( typeof window === 'undefined' ) return
  window.localStorage.removeItem( LOCAL_STORAGE_KEY )
}
